import clsx from 'clsx';
import React from 'react';
import { BiSolidTrash } from 'react-icons/bi';

import { getNodeTypeLabel } from '@/app/agents/edit/util/nodeTypeUtils';
import { Button } from '@/components/button/button';
import { Text } from '@/components/typography/text';
import {
  AgentConfigGlobalContexts,
  AgentConfigStep,
  AgentConfigStepAction,
  AgentConfigStepAgent,
  AgentConfigStepApiAction,
  AgentConfigStepInputs,
  AgentConfigStepLLMAction,
  AgentConfigStepLLMActionTool,
  AgentConfigStepRoutes,
  AgentConfigStepTypeEnum,
  AgentConfigStepVectorDBAction
} from '@/types/agent';

import ActionAgent from './actionAgent';
import ActionApi from './actionApi';
import ActionLLM from './actionLLM';
import ActionVectorDB from './actionVectorDB';
import PaneGlobalContexts from './paneGlobalContext';
import PaneInputs from './paneInput';
import PaneRoute from './paneRoute';


export type PanelActionProps = {
  editable: boolean;
  globalContext?: AgentConfigGlobalContexts;
  modelNames: string[];
  onChangeAction: <T extends AgentConfigStepAction>( action: T ) => void;
  onChangeDescription: ( event: React.ChangeEvent<HTMLTextAreaElement> ) => void;
  onChangeInputs: ( inputs: AgentConfigStepInputs ) => void;
  onChangeName: ( event: React.FormEvent<HTMLInputElement> ) => void;
  onChangeRoutes: ( routes: AgentConfigStepRoutes ) => void;
  onDeleteClick: () => void;
  step: AgentConfigStep;
  stepNames: string[];
};

const PanelAction = React.memo( function PanelAction( {
  editable,
  globalContext = {},
  modelNames,
  onChangeAction,
  onChangeDescription,
  onChangeInputs,
  onChangeName,
  onChangeRoutes,
  onDeleteClick,
  step,
  stepNames
}: PanelActionProps ) {
  let toolsToInclude: AgentConfigStepLLMActionTool[] = [];
  if (
    typeof step.action !== 'undefined' &&
    typeof step.action.tools !== 'undefined'
  ) {
    toolsToInclude = step.action.tools;
  }

  return (
    <>
      <div className="configPane" data-testid='config-edit-panel-action'>
        <Text as="div" className='paneTitle' size="md" weight="bold">Node Details</Text>
        <Text as="label" size="sm" htmlFor="node-name">{ getNodeTypeLabel( step.type ) } Node Name</Text>
        <input
          id="node-name"
          data-testid="config-edit-panel-name-input"
          value={ step.id }
          type="text"
          onInput={ onChangeName }
          disabled={ !editable }
        />
        <Text as="label" size="sm" htmlFor="node-description">{ getNodeTypeLabel( step.type ) } Node Description</Text>
        <textarea
          id="node-description"
          data-testid="config-edit-panel-description-textarea"
          value={ step.description }
          onInput={ onChangeDescription }
          disabled={ !editable }
        />
      </div>
      <PaneGlobalContexts
        data-testid='config-edit-panel-start-contexts'
        editable={ false }
        globalContext={ globalContext }
        onContextsChange={ () => null }
        stepType={ step.type }
      />
      <PaneInputs
        data-testid='config-edit-panel-action-inputs'
        editable={ false }
        inputs={ step.inputs }
        onInputsChange={ onChangeInputs }
        stepId={ step.id }
        stepType={ step.type }
      />
      {
        step.type === AgentConfigStepTypeEnum.AGENT && (
          <ActionAgent
            agentAction={ step.action as AgentConfigStepAgent }
            editable={ editable }
            inputs={ step.inputs }
            onActionUpdate={ onChangeAction }
            stepId={ step.id }
          />
        )
      }
      {
        step.type === AgentConfigStepTypeEnum.LLM && (
          <ActionLLM
            llmAction={ step.action as AgentConfigStepLLMAction }
            editable={ editable }
            inputs={ step.inputs }
            models={ modelNames }
            onActionUpdate={ onChangeAction }
            stepId={ step.id }
          />
        )
      }
      {
        step.type === AgentConfigStepTypeEnum.API && (
          <ActionApi
            apiAction={ step.action as AgentConfigStepApiAction }
            editable={ editable }
            inputs={ step.inputs }
            onActionUpdate={ onChangeAction }
            stepId={ step.id }
          />
        )
      }
      {
        step.type === AgentConfigStepTypeEnum.VECTORDB && (
          <ActionVectorDB
            dbAction={ step.action as AgentConfigStepVectorDBAction }
            editable={ editable }
            inputs={ step.inputs }
            onActionUpdate={ onChangeAction }
          />
        )
      }
      <PaneRoute
        editable={ editable }
        tools={ toolsToInclude }
        inputs={ step.inputs }
        onRoutesChange={ onChangeRoutes }
        routes={ step.routes }
        stepId={ step.id }
        steps={ stepNames }
      />
      {
        editable && (
          <div className="deleteSection">
            <div className={ clsx( 'layout__row', 'pad', 'space_around' ) }>
              <Button data-testid="config-edit-panel-delete-node-button" onClick={ onDeleteClick } variant='secondary'>
                <BiSolidTrash />
                Delete Node
              </Button>
            </div>
          </div>
        )
      }
    </>
  );
} );

export default PanelAction;
